import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Download,
  ExternalLink,
  ArrowUpDown,
  Link2,
  TrendingUp,
  Eye,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { ScrollArea } from '../../../../../components/ui/scroll-area';
import { cn } from '../../../../../lib/utils';
import useLinksViewsData from '../../components/DashboardLinkViews/hooks/useLinkViewsData';
import useHeadDepartment from '../../hooks/useHeadDepartment';

function getLinkDomain(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
}

function getLinkName(url) {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      const lastPart = pathParts[pathParts.length - 1];
      return decodeURIComponent(lastPart).replace(/[-_]/g, ' ');
    }
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
}

function LinkViewsCard({ startPickerValue, endPickerValue }) {
  const {
    linkViewsData,
    isLoading,
    totalCount,
    rowsPerPage,
    currentPage,
    order,
    orderBy,
    handleChangePagination,
    handleChangeOrder,
  } = useLinksViewsData(startPickerValue, endPickerValue);

  const { departmentId } = useHeadDepartment();

  const exportUrl = new URL(
    '/api/v1/users-history/export/links-views',
    process.env.REACT_APP_API_URL,
  );
  exportUrl.searchParams.set('order', order === 'asc' ? 1 : -1);
  exportUrl.searchParams.set('orderBy', orderBy);
  exportUrl.searchParams.set('createdAt[gte]', startPickerValue.toISOString());
  exportUrl.searchParams.set('createdAt[lte]', endPickerValue.toISOString());
  exportUrl.searchParams.set('departmentId', departmentId);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  // Calculate stats
  const stats = useMemo(() => {
    if (!linkViewsData.length) return { totalViews: 0, avgViews: 0, topLink: null };
    const totalViews = linkViewsData.reduce((acc, item) => acc + item.views, 0);
    const avgViews = Math.round(totalViews / linkViewsData.length);
    const topLink = linkViewsData[0];
    return { totalViews, avgViews, topLink };
  }, [linkViewsData]);

  // Find max views for progress bar calculation
  const maxViews = useMemo(() => {
    if (!linkViewsData.length) return 1;
    return Math.max(...linkViewsData.map((item) => item.views));
  }, [linkViewsData]);

  return (
    <Card style={{ border: '1px solid hsl(var(--border))' }} className="h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Link2 className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Most Visited Links</CardTitle>
              <p className="text-sm text-muted-foreground">Popular learning resources</p>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={exportUrl.toString()} download>
              <Download className="h-4 w-4 mr-2" />
              Export
            </a>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col space-y-4">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Total Views</span>
            </div>
            <div className="text-xl font-bold">{stats.totalViews}</div>
          </div>
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Avg Views</span>
            </div>
            <div className="text-xl font-bold">{stats.avgViews}</div>
          </div>
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 mb-1">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Total Links</span>
            </div>
            <div className="text-xl font-bold">{totalCount}</div>
          </div>
        </div>

        {/* Sort Button */}
        <div className="flex justify-end">
          <button
            type="button"
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium',
              'bg-muted hover:bg-muted/80 transition-colors',
            )}
            onClick={() => handleChangeOrder('viewsCount')}
          >
            Sort by views
            {orderBy === 'viewsCount' && order === 'asc' ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )}
          </button>
        </div>

        {/* Links List */}
        <ScrollArea className="flex-1 -mx-2 px-2">
          <div className="space-y-2">
            {isLoading &&
              Array.from({ length: 5 }).map((_, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="p-3 rounded-lg border bg-card animate-pulse"
                  style={{ borderColor: 'hsl(var(--border))' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 w-3/4 bg-muted rounded" />
                      <div className="h-3 w-1/2 bg-muted rounded" />
                    </div>
                    <div className="h-8 w-12 bg-muted rounded-full" />
                  </div>
                </div>
              ))}

            {!isLoading && linkViewsData.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Link2 className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No links visited yet</p>
                <p className="text-xs text-muted-foreground/70">
                  Data will appear when users start clicking links
                </p>
              </div>
            )}

            {!isLoading &&
              linkViewsData.length > 0 &&
              linkViewsData.map((item, index) => (
                <a
                  key={item._id}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'block p-3 rounded-lg border bg-card transition-all',
                    'hover:bg-muted/50 hover:border-primary/30 group',
                  )}
                  style={{ borderColor: 'hsl(var(--border))' }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-muted-foreground">
                          #{index + 1 + currentPage * rowsPerPage}
                        </span>
                        <span className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                          {getLinkName(item.link)}
                        </span>
                        <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {getLinkDomain(item.link)}
                      </p>
                      {/* Progress bar showing relative popularity */}
                      <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all"
                          style={{ width: `${(item.views / maxViews) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-600">
                        <Eye className="h-3 w-3" />
                        <span className="text-sm font-semibold">{item.views}</span>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
          </div>
        </ScrollArea>

        {/* Pagination */}
        {totalCount > rowsPerPage && (
          <div
            className="flex items-center justify-between pt-4 border-t"
            style={{ borderColor: 'hsl(var(--border))' }}
          >
            <p className="text-xs text-muted-foreground">
              {currentPage * rowsPerPage + 1}-
              {Math.min((currentPage + 1) * rowsPerPage, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleChangePagination(null, currentPage - 1)}
                disabled={currentPage === 0}
                className="h-7 px-2"
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleChangePagination(null, currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="h-7 px-2"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

LinkViewsCard.propTypes = {
  startPickerValue: PropTypes.instanceOf(dayjs).isRequired,
  endPickerValue: PropTypes.instanceOf(dayjs).isRequired,
};

export default LinkViewsCard;
